import type { CSSProperties } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

import type { SanityTestimonial } from '#/lib/sanity/queries/testimonial';
import type { SanityTestimonialCarousel } from '#/lib/sanity/queries/testimonialCarousel';
import {
  getSectionThemeSurface,
  normalizeSectionTheme,
  type SectionTheme,
} from '#/lib/section-theme';
import { cn } from '#/lib/utils';

import { formatHeadline } from './Headline';
import {
  QuoteMark,
  TestimonialLogo,
  UeVerifiedBadge,
} from './TestimonialParts';

// Card / type colours sit on top of the shared section surface map.
const THEME_STYLES: Record<
  SectionTheme,
  {
    eyebrow: string;
    headline: string;
    highlight?: string;
    card: string;
    quoteMark?: string;
    quote: string;
    name: string;
    muted: string;
    ue: string;
    /** Fill colour for the masked customer-logo silhouette. */
    logo: string;
  }
> = {
  light: {
    eyebrow: 'text-cipher-05',
    headline: 'text-charcoal',
    highlight: undefined,
    card: 'bg-cloud',
    quoteMark: undefined,
    quote: 'text-cipher-06',
    name: 'text-charcoal',
    muted: 'text-grayscale-04',
    ue: 'text-charcoal',
    logo: 'bg-charcoal',
  },
  white: {
    eyebrow: 'text-cipher-05',
    headline: 'text-charcoal',
    highlight: undefined,
    card: 'bg-grayscale-01',
    quoteMark: undefined,
    quote: 'text-cipher-06',
    name: 'text-charcoal',
    muted: 'text-grayscale-04',
    ue: 'text-charcoal',
    logo: 'bg-charcoal',
  },
  purple: {
    eyebrow: 'text-electric',
    headline: 'text-cloud',
    highlight: 'text-electric',
    card: 'bg-cipher-600',
    quoteMark: 'text-cipher-03',
    quote: 'text-cloud',
    name: 'text-cloud',
    muted: 'text-cloud',
    ue: 'text-cloud',
    logo: 'bg-cloud',
  },
  charcoal: {
    eyebrow: 'text-electric',
    headline: 'text-cloud',
    highlight: 'text-electric',
    card: 'bg-grayscale-06',
    // The QuoteMark default (cipher-50 lavender) reads well on charcoal too;
    // electric was too loud here.
    quoteMark: undefined,
    quote: 'text-cloud',
    name: 'text-cloud',
    muted: 'text-grayscale-03',
    ue: 'text-cloud',
    logo: 'bg-cloud',
  },
};

function TestimonialCard({
  testimonial,
  theme,
}: {
  testimonial: SanityTestimonial;
  theme: SectionTheme;
}) {
  const ueUrl = testimonial.ueUrl ? new URL(testimonial.ueUrl) : null;
  const styles = THEME_STYLES[theme];

  return (
    <div
      className={cn(
        'flex h-full flex-col gap-20 rounded-16 px-20 py-40 l:gap-40 l:px-50 l:py-60 xl:px-80 xl:py-100',
        styles.card,
      )}
    >
      <QuoteMark
        className={cn('w-40 shrink-0 m:w-50 l:w-67', styles.quoteMark)}
      />
      <p
        className={cn(
          'type-heading6 m:type-heading5 xl:type-heading4 text-pretty',
          styles.quote,
        )}
      >
        “{testimonial.quote}”
      </p>
      <div className="mt-auto flex flex-col gap-36 l:flex-row l:flex-wrap l:items-center l:justify-between l:gap-x-20 l:gap-y-36">
        <div className="flex flex-col items-start gap-8">
          {testimonial.image?.url ? (
            <TestimonialLogo
              image={testimonial.image}
              className={cn(
                'w-[min(calc(var(--logo-h)*var(--logo-ratio)),var(--logo-max-w,9999px))] [--logo-h:32px] l:[--logo-h:48px]',
                styles.logo,
              )}
              style={
                {
                  '--logo-ratio':
                    testimonial.image.width / testimonial.image.height,
                  ...(testimonial.logoMaxWidth
                    ? { '--logo-max-w': `${testimonial.logoMaxWidth}px` }
                    : null),
                } as CSSProperties
              }
            />
          ) : null}
          <div className="space-y-8">
            <p
              className={cn(
                'type-paragraph-lg font-medium trim-text',
                styles.name,
              )}
            >
              {testimonial.name}
            </p>
            {testimonial.role ? (
              <p className={cn('type-paragraph-sm trim-text', styles.muted)}>
                {testimonial.role}
              </p>
            ) : null}
          </div>
        </div>
        {ueUrl ? (
          <a
            className={cn(
              'flex flex-col items-start gap-8 transition-opacity hover:opacity-70',
              styles.ue,
            )}
            href={ueUrl.toString()}
            target="_blank"
            rel="noopener noreferrer"
          >
            <UeVerifiedBadge className="size-32 shrink-0" />
            <div className="space-y-8">
              <p className="type-paragraph-lg font-medium trim-text">
                UE Verified
              </p>
              <p className={cn('type-paragraph-sm trim-text', styles.muted)}>
                {ueUrl.hostname + ueUrl.pathname}
              </p>
            </div>
          </a>
        ) : null}
      </div>
    </div>
  );
}

export function TestimonialCarousel({
  theme: themeInput,
  eyebrow,
  headline,
  testimonials,
}: SanityTestimonialCarousel) {
  const theme = normalizeSectionTheme(themeInput);
  const styles = THEME_STYLES[theme];

  // The layout is designed for exactly two testimonials.
  const visibleTestimonials = testimonials.slice(0, 2);

  // Mobile/tablet (<1024px) uses embla for unified touch + mouse drag.
  const [emblaRef] = useEmblaCarousel(
    {
      align: 'start',
      containScroll: 'trimSnaps',
    },
    [WheelGesturesPlugin()],
  );

  return (
    <div className={cn('p-main py-40 l:py-100', getSectionThemeSurface(theme))}>
      <div className="fade-in mx-auto flex w-full max-w-1280 flex-col gap-24 l:gap-48">
        {eyebrow || headline ? (
          <div className="flex flex-col gap-16 l:gap-20">
            {eyebrow ? (
              <span className={cn('type-eyebrow block', styles.eyebrow)}>
                {eyebrow}
              </span>
            ) : null}
            {headline ? (
              <h2
                className={cn(
                  'type-heading5 l:type-heading3 text-balance',
                  styles.headline,
                )}
              >
                {formatHeadline(headline, styles.highlight)}
              </h2>
            ) : null}
          </div>
        ) : null}

        {/* Desktop (≥1024px): two static cards side by side */}
        <div className="hidden gap-42 l:grid l:grid-cols-2">
          {visibleTestimonials.map((testimonial) => (
            <TestimonialCard
              key={testimonial._key}
              testimonial={testimonial}
              theme={theme}
            />
          ))}
        </div>

        {/* Below 1024px: draggable slider (touch + mouse) via embla */}
        <div
          ref={emblaRef}
          className="-mx-15 cursor-grab overflow-hidden px-15 active:cursor-grabbing l:hidden"
        >
          <div className="flex gap-15">
            {visibleTestimonials.map((testimonial) => (
              <div key={testimonial._key} className="w-[82vw] shrink-0">
                <TestimonialCard testimonial={testimonial} theme={theme} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
